import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Tabs, Button, Space, notification } from 'antd'
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { contextCompanyIdSelector, contextCompanySelector } from '../../../../redux/context/company/selectors'
import { getCompanySettingsRequest, setCompanySettingsAction } from '../../../../redux/context/companySettings/actions'
import { contextRequest } from '../../../../redux/context/actions'
import { Company } from '../../../../types/company/Company'
import { CompanySettings } from '../../../../types/companySettings/CompanySettings'
import { filtersSelector } from '../../../../redux/context/filters/selectors'
import { AppDispatch } from '../../../../redux/store'
import {
  AccountMapRuleGrid,
  AccountMapRuleGridRef,
  CompanyColumnInfo,
  useUpdateAccountMapRules
} from '../../../../features/accountMap'
import { GroupSettingDataSource } from '../../../../features/companyGroup/types/CompanyGroupSettings'
import { useCtxCompanyGroupSettings } from '../../../../features/companyGroup/queries/companyGroupSetting/useCtxCompanyGroupSettings'
import { getCompanyGroupOptions } from '../../../../features/companyGroup/queries/getCompanyGroupOptions'
import { flattenGroup } from '../../../../features/companyGroup/utils/companyGroupUtils'
import accountMapQueryKeys from '../../../../features/accountMap/queries/accountMapQueryKeys'
import { groupedStatementRowsSelector } from '../../../../redux/entities/statementRows/selectors'
import CompanyFormCascader from '../../../../features/accountMap/components/CompanyFormCascader'
import useUnsavedChangesBlocker from '../../../../shared/hooks/useUnsavedChangesBlocker'

const CompanyAccountMap: React.FC = () => {
  const { t } = useTranslation()
  const dispatch: AppDispatch = useDispatch()

  const [isEditing, setIsEditing] = useState(false)
  const [activeTab, setActiveTab] = useState('income-statement')

  const incomeStatementGridRef = useRef<AccountMapRuleGridRef>(null)
  const assetsGridRef = useRef<AccountMapRuleGridRef>(null)
  const liabilitiesGridRef = useRef<AccountMapRuleGridRef>(null)

  const { mutate: updateRules, isPending: isSaving } = useUpdateAccountMapRules()
  const queryClient = useQueryClient()

  const companyId = useSelector(contextCompanyIdSelector)
  const company = useSelector(contextCompanySelector)
  const companyForm = company?.form
  const { budgetingScenario } = useSelector(filtersSelector)

  // Get statement rows grouped by report type (already in correct order from backend)
  const groupedStatementRows = useSelector(groupedStatementRowsSelector)
  const { INCOME_STATEMENT: incomeStatement, ASSETS: assets, LIABILITIES: liabilities } = groupedStatementRows

  // React Query - fetch group settings
  const { data: groupSettings } = useCtxCompanyGroupSettings()

  // React Query - fetch company group (only when needed)
  const { data: companyGroup } = useQuery({
    ...getCompanyGroupOptions(companyId!),
    enabled: !!companyId && groupSettings?.actuals?.source === GroupSettingDataSource.subsidiaries
  })

  useEffect(() => {
    companyId && dispatch(contextRequest(getCompanySettingsRequest))

    return () => {
      dispatch(setCompanySettingsAction({} as CompanySettings))
    }
  }, [companyId, dispatch, companyForm, budgetingScenario])

  // Reset grids and refetch rules when company form changes
  useEffect(() => {
    incomeStatementGridRef.current?.resetToServerData()
    assetsGridRef.current?.resetToServerData()
    liabilitiesGridRef.current?.resetToServerData()
    setIsEditing(false)
    if (companyId) {
      queryClient.invalidateQueries({ queryKey: accountMapQueryKeys.list(companyId) })
    }
  }, [companyForm, companyId, queryClient])

  // Determine companies based on group settings
  const companies = useMemo<CompanyColumnInfo[]>(() => {
    if (!companyId || !company) return []

    // If subsidiaries source, use flattened group
    if (groupSettings?.actuals?.source === GroupSettingDataSource.subsidiaries && companyGroup) {
      const flattened = flattenGroup(companyGroup as Company)
      return flattened.map(c => ({ companyId: c.id, companyName: c.name }))
    }

    // Default: use current company only
    return [{ companyId: company.id, companyName: company.name }]
  }, [companyId, company, groupSettings, companyGroup])

  const handleSave = () => {
    if (!companyId) return

    // Validate all grids
    const allValid = [incomeStatementGridRef, assetsGridRef, liabilitiesGridRef].every(
      ref => ref.current?.validate() !== false
    )

    if (!allValid) {
      notification.error({
        message: t(
          'accountMapPage:validationErrorPair',
          'From and To values must both be filled and From must be less than or equal to To'
        ),
        duration: 0
      })
      return
    }

    // Collect rules from all grids
    const incomeStatementRules = incomeStatementGridRef.current?.getRules() ?? []
    const assetsRules = assetsGridRef.current?.getRules() ?? []
    const liabilitiesRules = liabilitiesGridRef.current?.getRules() ?? []

    // Merge all rules
    const allRules = [...incomeStatementRules, ...assetsRules, ...liabilitiesRules]

    updateRules(
      { companyId, rules: allRules },
      {
        onSuccess: () => {
          incomeStatementGridRef.current?.discardChanges()
          assetsGridRef.current?.discardChanges()
          liabilitiesGridRef.current?.discardChanges()
          setIsEditing(false)
        }
      }
    )
  }

  const handleCancel = () => {
    // Discard changes from all grids
    incomeStatementGridRef.current?.discardChanges()
    assetsGridRef.current?.discardChanges()
    liabilitiesGridRef.current?.discardChanges()
    setIsEditing(false)
  }

  // Check if any grid has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    return (
      incomeStatementGridRef.current?.hasChanges() ||
      assetsGridRef.current?.hasChanges() ||
      liabilitiesGridRef.current?.hasChanges() ||
      false
    )
  }, [])

  // Block navigation when there are unsaved changes
  useUnsavedChangesBlocker({
    hasUnsavedChanges,
    onDiscard: handleCancel
  })

  if (!companyId || companies.length === 0) return null

  const tabItems = [
    {
      key: 'income-statement',
      label: t('financialStatementsPage:incomeStatement'),
      forceRender: true,
      children: (
        <AccountMapRuleGrid
          ref={incomeStatementGridRef}
          companies={companies}
          statementRows={incomeStatement}
          editable={isEditing}
        />
      )
    },
    {
      key: 'assets',
      label: t('financialStatementsPage:assets'),
      forceRender: true,
      children: (
        <AccountMapRuleGrid ref={assetsGridRef} companies={companies} statementRows={assets} editable={isEditing} />
      )
    },
    {
      key: 'liabilities',
      label: t('financialStatementsPage:liabilities'),
      forceRender: true,
      children: (
        <AccountMapRuleGrid
          ref={liabilitiesGridRef}
          companies={companies}
          statementRows={liabilities}
          editable={isEditing}
        />
      )
    }
  ]

  const tabBarExtraContent = (
    <Space>
      <CompanyFormCascader disabled={isEditing} />
      {!isEditing ? (
        <Button type="primary" icon={<EditOutlined />} onClick={() => setIsEditing(true)}>
          {t('actions:edit')}
        </Button>
      ) : (
        <>
          <Button icon={<CloseOutlined />} onClick={handleCancel}>
            {t('actions:cancel')}
          </Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={isSaving}>
            {t('actions:save')}
          </Button>
        </>
      )}
    </Space>
  )

  return <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} tabBarExtraContent={tabBarExtraContent} />
}

export default CompanyAccountMap
